import { useEffect, useState } from 'react';

/* ── Formatter ─────────────────────────────────────────────────── */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Format an ISO timestamp as a human-readable relative string.
 * Updates are handled by the component; this is a pure function.
 */
export function formatRelativeTime(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const diff = Date.now() - date.getTime();

  if (diff < 0) return 'just now'; // future timestamps
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) {
    const mins = Math.floor(diff / MINUTE);
    return `${mins}m ago`;
  }
  if (diff < DAY) {
    const hrs = Math.floor(diff / HOUR);
    return `${hrs}h ago`;
  }
  if (diff < WEEK) {
    const days = Math.floor(diff / DAY);
    return days === 1 ? 'yesterday' : `${days}d ago`;
  }

  // Older than a week — show date
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Determine the appropriate refresh interval based on the age.
 */
function getRefreshInterval(iso: string | Date): number {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const diff = Date.now() - date.getTime();

  if (diff < HOUR) return 30_000; // Update every 30s for recent items
  if (diff < DAY) return 5 * MINUTE; // Every 5min for today
  return 0; // Don't auto-update for older items
}

/* ── Component ─────────────────────────────────────────────────── */

interface TimeAgoProps {
  date: string | Date;
  className?: string;
  /** Show the full date/time as a tooltip (default: true) */
  tooltip?: boolean;
}

/**
 * Renders a relative timestamp that auto-updates.
 *
 * @example
 * <TimeAgo date={post.createdAt} className="text-xs text-neutral-400" />
 */
export function TimeAgo({ date, className, tooltip = true }: TimeAgoProps) {
  const [text, setText] = useState(() => formatRelativeTime(date));

  useEffect(() => {
    setText(formatRelativeTime(date));

    const interval = getRefreshInterval(date);
    if (interval === 0) return;

    const id = setInterval(() => setText(formatRelativeTime(date)), interval);
    return () => clearInterval(id);
  }, [date]);

  const fullDate = (typeof date === 'string' ? new Date(date) : date).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <time
      dateTime={typeof date === 'string' ? date : date.toISOString()}
      title={tooltip ? fullDate : undefined}
      className={className}
    >
      {text}
    </time>
  );
}
